/**
 * Fetches SMSWiser campaigns per country for multi-country catalog pickers.
 */

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { smswiserKeys, type SMSCampaign } from '@/hooks/smswiser/core/useSMSWiser';
import api from '@/services/api';

function unwrapApiData<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

function toSortedOptions(camps: SMSCampaign[]): Array<{ id: number; name: string }> {
  return [...camps]
    .map((c) => ({ id: c.id, name: c.name }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export function useCampaignsForCountryKeys(countryKeys: string[], enabled: boolean) {
  const sortedKeys = useMemo(
    () => [...new Set(countryKeys.filter(Boolean))].sort(),
    [countryKeys.join('\0')],
  );
  const queries = useQueries({
    queries: sortedKeys.map((key) => ({
      queryKey: [...smswiserKeys.country(key), 'campaigns'] as const,
      queryFn: async () => {
        const response = await api.get(`/smswiser/countries/${key}/campaigns`);
        return unwrapApiData<SMSCampaign[]>(response.data);
      },
      enabled: enabled && sortedKeys.length > 0 && !!key,
      staleTime: 3600000,
    })),
  });
  return useMemo(() => {
    const map: Record<string, Array<{ id: number; name: string }>> = {};
    sortedKeys.forEach((key, i) => {
      const raw = queries[i]?.data;
      if (raw?.length) map[key] = toSortedOptions(raw);
    });
    return map;
  }, [sortedKeys, queries]);
}
